import { Component, OnInit, OnDestroy, StateKey, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { MatButtonModule } from '@angular/material/button';
import { AuthState } from '../../../../core/models/auth.model';
import * as AuthActions from '../../../../core/store/auth/auth.actions';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { ScrollToTopComponent } from '../../../../shared/components/scroll-to-top/scroll-to-top.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { RouterLink } from '@angular/router';
import { PostsService } from '../../../../core/services/posts.service';
import { Post } from '../../../../core/models/post.model';
import { environment } from '../../../../../environments/environment';
import { GameService } from '../../../../core/services/game.service';
import { Game, GamesInCategories } from '../../../../core/models/game.model';
import { MatCardModule } from '@angular/material/card';
import { GameCardComponent } from '../../../../shared/components/game-card/game-card.component';
import { StartGameHelper } from '../../../../core/helpers/start-game.helper';
import { ScriptsService } from '../../../../core/services/script.service';
import { TransferState, makeStateKey } from '@angular/core';
import { TranslationService } from '../../../../core/services/translation.service';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { takeUntil, Subject, finalize, tap, shareReplay, switchMap, map, catchError, of, distinctUntilChanged, combineLatest } from 'rxjs';
import { langPart } from '../../../../core/helpers/navigation.helper';
import { MetaService } from '../../../../core/services/meta.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss', '../../public-pages.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    FooterComponent,
    HeaderComponent,
    RouterLink,
    MatCardModule,
    GameCardComponent,
    ScrollToTopComponent,
    TranslatePipe
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  roadMapEvents: Post[] = [];
  newsList: Post[] = [];
  version = environment.version;
  public categories: GamesInCategories = {};
  private readonly destroyRef = inject(DestroyRef);
  public cdn = environment.cdnUrl;

  ogImage = this.cdn + '/images/playing-charades.webp';

  ldData: any;
  langPart = '';
  private destroy$ = new Subject<void>();
  public gameLoading = false;
  public pageLoading = false;

  constructor(
    private store: Store<{ auth: AuthState }>,
    private postsService: PostsService,
    private gameService: GameService,
    private startGameHelper: StartGameHelper,
    private scriptsService: ScriptsService,
    private state: TransferState,
    private translationService: TranslationService,
    private metaService: MetaService,
  ) { }

  ngOnInit(): void {
    const lang$ = this.translationService.getCurrentLang().pipe(
      distinctUntilChanged(),
      tap(lang => this.langPart = langPart(lang)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const loadWithTS = <T>(
      key: StateKey<T>,
      fetch$: import('rxjs').Observable<T>,
      label: string
    ) => {
      const cached = this.state.get<T | null>(key, null);
      console.log(cached ? `✅ TransferState hit (${label})` : `🔁 Fetching ${label} from API`);
      return cached ? of(cached) : fetch$.pipe(tap(v => this.state.set(key, v)));
    };

    // React to language changes: fetch all sections in parallel each time
    lang$.pipe(
      switchMap(lang => {
        this.pageLoading = true;

        const games$ = loadWithTS<GamesInCategories>(
          makeStateKey<GamesInCategories>(`games-${lang}`),
          this.gameService.getGamesInCategory('home', lang, 'my_en', false),
          'games'
        ).pipe(catchError(() => of({} as GamesInCategories)));

        const roadmap$ = loadWithTS<Post[]>(
          makeStateKey<Post[]>(`roadmap-${lang}`),
          this.postsService.getPosts({
            pageType: 'roadmap',
            period: 'future',
            order: 'ASC',
            limit: 1,
            lang
          }),
          'roadmap'
        ).pipe(catchError(() => of([] as Post[])));

        const news$ = loadWithTS<Post[]>(
          makeStateKey<Post[]>(`news-${lang}`),
          this.postsService.getPosts({
            pageType: 'news',
            period: 'all',
            order: 'DESC',
            limit: 1,
            lang
          }),
          'news'
        ).pipe(catchError(() => of([] as Post[])));

        return combineLatest({ games: games$, roadmap: roadmap$, news: news$ }).pipe(
          finalize(() => { this.pageLoading = false; })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ games, roadmap, news }) => {
      this.setupGames(games);
      this.roadMapEvents = roadmap;
      this.newsList = news;
    });

    // Meta + structured data also react to language changes
    const translationKeys = [
      'home.meta.title',
      'home.meta.description',
      'home.meta.keywords',
      'home.meta.url'
    ];

    lang$.pipe(
      switchMap(() => this.translationService.translateBatch(translationKeys)),
      takeUntil(this.destroy$)
    ).subscribe(([title, description, keywords, url]) => {
      this.metaService.updateGameMeta({
        title,
        description,
        url,
        imageUrl: this.ogImage,
        keywords
      }, false);

      this.ldData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Xedoom',
        url,
        description,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${url}/quizzes/text/{search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      };
      this.scriptsService.setStructuredDataScript(this.ldData);
    });
  }

  setupGames(categories: GamesInCategories): void {
    this.categories = categories;

    if (this.categories.publicQuizzes && this.categories.publicQuizzes.list.length > 0 && this.categories.publicQuizzes.list[0].coverImage) {
      this.scriptsService.preloadCoverImage(this.categories.publicQuizzes.list[0].coverImage.url);
    }
  }

  ngOnDestroy(): void {
    this.metaService.cleanUp();
    this.scriptsService.removeStructuredDataScript();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogout(): void {
    this.store.dispatch(AuthActions.logout());
  }

  onGameStart(gameId: number): void {
    if (this.gameLoading) return;
    this.gameLoading = true;
    this.startGameHelper.startGame(gameId).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.gameLoading = false)
    ).subscribe();
  }
}
